<template>
  <header class="bg-primary border-bottom border-light">
    <nav :class="['navbar navbar-expand-lg', environment === 'local' ? 'bg-success' : environment === 'staging' ? 'bg-danger' : '']">
      <div class="container-fluid">
        <router-link :to="isAuthenticated ? '/monitoring/kpis' : '/'" class="navbar-brand text-light" :class="breadcrumbs ? 'border-end border-2 pe-3' : ''">
          <svg id="navbar-logo" class="bi pb-1 me-2" width="30" height="30" fill="currentColor">
            <use xlink:href="@/assets/icons/icons.svg#nut-fill"/>
          </svg>
          <span v-if="isAuthenticated">{{ userOrg }}</span>
          <span v-else>
            <span v-if="!isMultiTenant">{{ tenantName }} @ </span>
            focusMT
          </span>
        </router-link>

        <!-- Breadcrumbs & Info-Link -->
        <div class="d-flex justify-content-between align-items-center">
          <nav v-if="breadcrumbs" id="nav-breadcrumbs" aria-label="breadcrumb">
            <ol class="breadcrumb my-auto">
              <li v-for="(crumb, index) in breadcrumbs.breadcrumbs" :key="'breadcrumb' + index" class="breadcrumb-item text-light" > {{ crumb }} </li>
            </ol>
          </nav>
          <DocsInfoLink v-if="breadcrumbs?.docsTarget" class="ms-3" color="white" :target="breadcrumbs.docsTarget" />
        </div>

        <button class="navbar-toggler pe-0" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation">
          <svg class="bi" width="30" height="30" fill="#f8f9fa">
            <use xlink:href="@/assets/icons/icons.svg#list"/>
          </svg>
        </button>
        <div class="collapse navbar-collapse" id="navbarSupportedContent">
          <ul v-if="isAuthenticated" class="navbar-nav ms-auto mb-2 mb-lg-0">
            <!-- Strategies & Objectives -->
            <li class="nav-item dropdown my-auto px-2">
              <a class="nav-link dropdown text-light" href="#" id="navbarDropdownObjective" role="button" data-bs-toggle="dropdown" aria-expanded="false">{{ $t("views.nav.headingObjectives") }}</a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownStrategy">
                <li><router-link to="/strategies/manage/visions" class="dropdown-item">{{ $t("views.nav.manageVisions") }}</router-link></li>
                <li><router-link to="/strategies/manage/strategies" class="dropdown-item">{{ $t("views.nav.manageStrategies") }}</router-link></li>
                <li><router-link to="/strategies/strategy-map" class="dropdown-item">{{ $t("views.nav.strategyMap") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link to="/objectives/manage/objectives" class="dropdown-item">{{ $t("views.nav.manageObjectives") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link @click="setKPIRecordMode('objective')" to="/monitoring/manage/kpis/records" class="dropdown-item">{{ $t("views.nav.recordObjective") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link to="/objectives/x-matrix" class="dropdown-item">{{ $t("views.nav.viewXMatrix") }}</router-link></li>
              </ul>
            </li>
            <!-- Monitoring -->
            <li class="nav-item dropdown my-auto px-2">
              <a class="nav-link dropdown text-light" href="#" id="navbarDropdownMonitoring" role="button" data-bs-toggle="dropdown" aria-expanded="false">{{ $t("views.nav.headingMonitoring") }}</a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownMonitoring">
                <li><router-link to="/monitoring/kpis/cockpit" class="dropdown-item">{{ $t("views.nav.cockpit") }}</router-link></li>
                <li><router-link to="/monitoring/kpis" class="dropdown-item">{{ $t("views.nav.viewKPIs") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link to="/monitoring/manage/kpis" class="dropdown-item">{{ $t("views.nav.manageKPIs") }}</router-link></li>
                <li><router-link to="/monitoring/manage/kpisets" class="dropdown-item">{{ $t("views.nav.manageKPISets") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link @click="setKPIRecordMode('actual')" to="/monitoring/manage/kpis/records" class="dropdown-item">{{ $t("views.nav.recordActualForecast") }}</router-link></li>
                <li><router-link @click="setKPIRecordMode('plan')" to="/monitoring/manage/kpis/records" class="dropdown-item">{{ $t("views.nav.recordPlan") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link to="/monitoring/kpis/deviations" class="dropdown-item">{{ $t("views.nav.deviations") }}</router-link></li>
                <li><router-link to="/monitoring/kpis/changes" class="dropdown-item">{{ $t("views.nav.changes") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link to="/measures/measureTracking" class="dropdown-item">{{ $t("views.nav.measureTracking") }}</router-link></li>
              </ul>
            </li>
            <!-- Projects -->
            <li class="nav-item dropdown my-auto px-2">
              <a class="nav-link dropdown text-light" href="#" id="navbarDropdownProject" role="button" data-bs-toggle="dropdown" aria-expanded="false">{{ $t("views.nav.headingProjects") }}</a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownProject">
                <li><router-link to="/projects/manage/projects" class="dropdown-item">{{ $t("views.nav.manageProjects") }}</router-link></li>
              </ul>
            </li>
            <!-- Organisation -->
            <li class="nav-item dropdown my-auto ps-2 pe-1">
              <a class="nav-link dropdown text-light" href="#" id="navbarDropdownOrg" role="button" data-bs-toggle="dropdown" aria-expanded="false">{{ $t("views.nav.headingOrg") }}</a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownOrg">
                <li><router-link to="/organization/manage/org-units" class="dropdown-item">{{ $t("views.nav.manageOrgs") }}</router-link></li>
                <li><router-link to="/organization/manage/employees" class="dropdown-item">{{ $t("views.nav.manageEmployees") }}</router-link></li>
              </ul>
            </li>
            <!-- My Focus -->
            <li class="nav-item dropdown my-auto ps-2 pe-1">
              <a class="nav-link dropdown text-light" href="#" id="navbarDropdownMyFocus" role="button" data-bs-toggle="dropdown" aria-expanded="false">{{ $t("views.nav.headingMyFocus") }}</a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownMyFocus">
                <li><router-link to="/my/tasks" class="dropdown-item">{{ $t("views.nav.myTasks") }}</router-link></li>
                <li><router-link to="/my/responsibilities" class="dropdown-item">{{ $t("views.nav.myResponsibilities") }}</router-link></li>
                <li><router-link to="/my/roles" class="dropdown-item">{{ $t("views.nav.myRoles") }}</router-link></li>
              </ul>
            </li>
            <li class="border-start border-2 mx-2"></li>
            <!-- Visual Navigation (OffCanvas) -->
            <li class="nav-item my-auto ps-1">
              <button class="nav-link btn p-0 d-flex align-items-center" data-bs-toggle="offcanvas" data-bs-target="#navigatorOffcanvas">
                <svg class="bi me-1" width="22" height="22" fill="#FFFFFF"> <title>Visual Navigation</title> <use xlink:href="@/assets/icons/icons.svg#sidebar"/> </svg>
              </button>
            </li>
            <!-- System -->
            <li v-if="role === 10387" class="nav-item dropdown my-auto ps-1">
              <a class="nav-link dropdown text-light" href="#" id="navbarDropdownSettings" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                <svg class="bi" width="22" height="22" fill="currentColor">
                  <use xlink:href="@/assets/icons/icons.svg#gear-fill"/>
                </svg>
              </a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownSettings">
                <li><router-link to="/settings/permissions" class="dropdown-item">{{ $t("views.nav.managePermissions") }}</router-link></li>
                <li><router-link to="/settings/tenant" class="dropdown-item">{{ $t("views.nav.manageTenant") }}</router-link></li>
                <li class="dropdown-divider"></li>
                <li><router-link to="/settings/sysparm" class="dropdown-item">{{ $t("views.nav.manageSysParms") }}</router-link></li>
              </ul>
            </li>
            <!-- Account -->
            <li class="nav-item dropdown my-auto">
              <a class="nav-link dropdown text-light pe-0" href="#" id="navbarDropdownUser" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                <svg class="bi" width="28" height="28" fill="currentColor">
                  <use xlink:href="@/assets/icons/icons.svg#person-fill"/>
                </svg>
              </a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownUser">
                <li><router-link to="/account/manage/user" class="dropdown-item">{{ $t("views.nav.manageAccount") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li v-for="org in userOrgs" :key="org.id">
                  <button @click="setOrg(org.id)" class="dropdown-item">
                    <span :class="org.active ? 'border-start border-primary border-3 me-1' : 'border-start border-3 me-1'"></span>
                    <span>{{ org.name }}</span>
                  </button>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li><button @click="logout" class="dropdown-item">{{ $t("views.nav.logout") }}</button></li>
                <li><hr class="dropdown-divider"></li>
                <li><a href="mailto:support@focusmt.de" class="dropdown-item">{{ $t("views.nav.support") }}</a></li>
                <li><hr class="dropdown-divider"></li>
                <li><router-link to="/settings/downloads" class="dropdown-item">{{ $t("views.nav.downloads") }}</router-link></li>
                <li><hr class="dropdown-divider"></li>
                <li><a :href="createLink('viewDocumentation')" target="_blank" class="dropdown-item">{{ $t("views.nav.docs") }}</a></li>
                <li><a :href="createLink('viewGlossary')" target="_blank" class="dropdown-item">{{ $t("views.nav.glossary") }}</a></li>
                <li><a :href="createLink('viewReleaseNotes')" target="_blank" class="dropdown-item">{{ $t("views.nav.releases") }}</a></li>
              </ul>
            </li>
          </ul>
          <ul v-if="!isAuthenticated" class="navbar-nav mb-2 mb-lg-0" :class="['LoginPage', 'RegisterPage'].includes(currentRoute) ? 'ms-auto' : ''">
            <li v-if="isMultiTenant" class="nav-item"><router-link to="/register" class="nav-link text-light pe-3 mt-3 mt-lg-0">{{ $t("views.nav.register") }}</router-link></li>
            <li class="nav-item"><router-link to="/login" class="nav-link text-light pe-0 mt-3 mt-lg-0">{{ $t("views.nav.login") }}</router-link></li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import DocsInfoLink from '@/components/DocsInfoLink.vue'

/* ---------------------------------------------
   Setup: Router, Store, I18n
--------------------------------------------- */
const route = useRoute()
const store = useStore()
const { t } = useI18n()

/* ---------------------------------------------
   Static data
--------------------------------------------- */
const targets = {
  viewDocumentation: {
    de: 'docs',
    en: 'en/docs'
  },
  viewGlossary: {
    de: 'docs/glossary',
    en: 'en/docs/glossary'
  },
  viewReleaseNotes: {
    de: 'docs/releases',
    en: 'en/docs/releases'
  }
}

/* ---------------------------------------------
   Computed Properties
--------------------------------------------- */
const isDE = computed(() => store.state.user.settings.language?.includes('de'))
const isAuthenticated = computed(() => store.state.user.email)
const isMultiTenant = computed(() => store.state.tenantMode === 'Multi')
const tenantName = computed(() => store.state.tenantName)

const currentRoute = computed(() => route.name)
const kpiRecordMode = computed(() => store.state.kpiRecordMode)

const role = computed(() => store.state.user.role)
const userOrgs = computed(() => store.state.userOrgs)
const userOrg = computed(() => store.state.userOrg)

const environment = computed(() => {
  switch (location.hostname) {
    case 'localhost': return 'local'
    case '127.0.0.1': return 'local'
    case 'staging.focusmt.de': return 'staging'
    case 'focusmt.de': return 'production'
    default: return 'unknown'
  }
})

/* ---------------------------------------------
   Breadcrumbs (komplexe Logik)
--------------------------------------------- */
const breadcrumbs = computed(() => {
  const map = {
    StrategiesManageVision: {
      breadcrumbs: [t('views.nav.headingStrategies'), t('views.nav.manageVisions')],
      docsTarget: 'manageVisions'
    },
    StrategiesManageStrategy: {
      breadcrumbs: [t('views.nav.headingStrategies'), t('views.nav.manageStrategies')],
      docsTarget: 'manageStrategies'
    },
    StrategiesStrategyMap: {
      breadcrumbs: [t('views.nav.headingStrategies'), t('views.nav.strategyMap')],
      docsTarget: 'manageStrategies'
    },
    ObjectivesManageObjective: {
      breadcrumbs: [t('views.nav.headingObjectives'), t('views.nav.manageObjectives')],
      docsTarget: 'viewObjective'
    },
    ObjectivesXMatrix: {
      breadcrumbs: [t('views.nav.headingObjectives'), t('views.nav.viewXMatrix')],
      docsTarget: 'viewXmatrix'
    },
    MonitoringCockpit: {
      breadcrumbs: [t('views.nav.headingMonitoring'), t('views.nav.cockpit')],
      docsTarget: 'cockpit'
    },
    MonitoringKPI: {
      breadcrumbs: [t('views.nav.headingMonitoring'), t('views.nav.viewKPIs')],
      docsTarget: 'viewKPIs'
    },
    MonitoringManageKPI: {
      breadcrumbs: [t('views.nav.headingMonitoring'), t('views.nav.manageKPIs')],
      docsTarget: 'manageKPIs'
    },
    MonitoringManageKPISet: {
      breadcrumbs: [t('views.nav.headingMonitoring'), t('views.nav.manageKPISets')],
      docsTarget: 'manageKPISets'
    },
    MonitoringKPIDeviation: {
      breadcrumbs: [t('views.nav.headingMonitoring'), t('views.nav.deviations')],
      docsTarget: 'kpiDeviation'
    },
    MonitoringKPIChange: {
      breadcrumbs: [t('views.nav.headingMonitoring'), t('views.nav.changes')],
      docsTarget: 'kpiChange'
    },
    MeasureTracking: {
      breadcrumbs: [t('views.nav.headingMonitoring'), t('views.nav.measureTracking')],
      docsTarget: 'measureTracking'
    },
    ProjectsManageProject: {
      breadcrumbs: [t('views.nav.headingProjects'), t('views.nav.manageProjects')],
      docsTarget: 'manageProjects'
    },
    OrgManageOrgUnit: {
      breadcrumbs: [t('views.nav.headingOrg'), t('views.nav.manageOrgs')],
      docsTarget: 'viewOrg'
    },
    OrgManageEmployee: {
      breadcrumbs: [t('views.nav.headingOrg'), t('views.nav.manageEmployees')],
      docsTarget: 'manageEmployees'
    },
    MyTasks: {
      breadcrumbs: [t('views.nav.headingMyFocus'), t('views.nav.myTasks')],
      docsTarget: 'my'
    },
    MyResponsibilities: {
      breadcrumbs: [t('views.nav.headingMyFocus'), t('views.nav.myResponsibilities')],
      docsTarget: 'my'
    },
    MyRoles: {
      breadcrumbs: [t('views.nav.headingMyFocus'), t('views.nav.myRoles')],
      docsTarget: 'my'
    },
    SettingsManagePermission: {
      breadcrumbs: [t('views.nav.headingSettings'), t('views.nav.managePermissions')],
      docsTarget: 'managePermissions'
    },
    SettingsManageSysParm: {
      breadcrumbs: [t('views.nav.headingSettings'), t('views.nav.manageSysParms')],
      docsTarget: 'manageSettings'
    },
    AccountManageUser: {
      breadcrumbs: [t('views.nav.manageAccount')],
      docsTarget: 'manageAccount'
    },
    SettingsManageTenant: {
      breadcrumbs: [t('views.nav.headingSettings'), t('views.nav.manageTenant')],
      docsTarget: 'manageAccount'
    },
    SettingsDownloadDocuments: {
      breadcrumbs: [t('views.nav.downloads')],
      docsTarget: 'downloadDocuments'
    }
  }

  // Spezialfall: MonitoringManageKPIRecord
  if (currentRoute.value === 'MonitoringManageKPIRecord') {
    let docsTarget = null
    let kpiLabel = ''

    if (kpiRecordMode.value === 'actual') {
      kpiLabel = t('views.nav.recordActualForecast')
      docsTarget = 'manageKPIRecordActual'
    } else if (kpiRecordMode.value === 'plan') {
      kpiLabel = t('views.nav.recordPlan')
      docsTarget = 'manageKPIRecordPlan'
    } else if (kpiRecordMode.value === 'objective') {
      kpiLabel = t('views.nav.recordObjective')
      docsTarget = 'manageKPIRecordObjective'
    }

    return {
      breadcrumbs: [t('views.nav.headingMonitoring'), kpiLabel],
      docsTarget
    }
  }

  // Standardfall
  return map[currentRoute.value] || false
})

/* ---------------------------------------------
   Methods → normale Funktionen
--------------------------------------------- */
async function setOrg (id) {
  store.commit('SET_LOADING_STATUS', true)
  await store.dispatch('setOrg', { org: id })
  store.commit('SET_LOADING_STATUS', false)
}

function setKPIRecordMode (mode) {
  store.dispatch('setKPIRecordMode', mode)
}

function createLink (item) {
  const baseUrl = 'https://focusmt.de/'
  return isDE.value
    ? baseUrl + targets[item].de
    : baseUrl + targets[item].en
}

function logout () {
  store.dispatch('logout')
}
</script>

<style scoped>
nav button {
  border: none;
}
.navbar .navbar-brand {
  max-height: 40px;
}
#nav-breadcrumbs {
  --bs-breadcrumb-divider: '>';
}
#nav-breadcrumbs .breadcrumb-item::before {
  color: #f8f9fa;
}
</style>
